import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiClient } from '@/lib/api-client';
import { ComparisonStatus, CreateComparisonRequest } from '@/api/generated/client/models';
import { normalizeLogs } from '@/lib/reasoning-log-utils';
import { useComparisonMatchingSSE } from './useSSEProgress';
import { useToast } from '@/hooks/use-toast';

export function useComparisons(claimId?: number) {
    const queryClient = useQueryClient();
    const { data: response, isLoading } = useQuery({
        queryKey: ['comparisons', claimId ? { claimId } : 'list'],
        queryFn: async () => {
            return await apiClient.comparisons.getComparisons({
                page: 1,
                limit: 100, // Fetch arbitrary limit for now
                ...(claimId ? { claimId } : {})
            });
        },
        refetchInterval: (query) => {
            const data = query.state.data;
            if (!data?.items) return false;

            const hasPending = data.items.some(c =>
                c.status === ComparisonStatus.Processing
            );
            return hasPending ? 5000 : false;
        }
    });

    const comparisons = response?.items || [];

    // Create a map for easy lookup by claimId (latest comparison per claim)
    const comparisonsByClaimId = comparisons.reduce((acc, comp) => {
        if (!comp.claimId) return acc;
        // Logic to keep the most relevant comparison, for now we just overwrite
        // assuming list might be ordered or we just want *one* for the table view.
        acc[comp.claimId] = comp;
        return acc;
    }, {} as Record<number, typeof comparisons[0]>);

    const createComparisonMutation = useMutation({
        mutationFn: async (data: {
            insurerEstimateId?: number,
            contractorEstimateId?: number,
            insurerDocumentId?: number,
            contractorDocumentId?: number
        }) => {
            if (!claimId) throw new Error("Claim ID is required");
            const createComparisonRequest: CreateComparisonRequest = {
                claimId,
            };
            if (data.insurerEstimateId) {
                createComparisonRequest.insurerEstimateId = data.insurerEstimateId;
            }
            if (data.contractorEstimateId) {
                createComparisonRequest.contractorEstimateId = data.contractorEstimateId;
            }
            if (data.insurerDocumentId) {
                createComparisonRequest.insurerDocumentId = data.insurerDocumentId;
            }
            if (data.contractorDocumentId) {
                createComparisonRequest.contractorDocumentId = data.contractorDocumentId;
            }

            return await apiClient.comparisons.createComparison({
                createComparisonRequest
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comparisons'] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    });

    return {
        comparisons,
        comparisonsByClaimId,
        isLoading,
        createComparison: createComparisonMutation.mutate,
        createComparisonAsync: createComparisonMutation.mutateAsync,
        isCreating: createComparisonMutation.isPending
    };
}

export function useDeleteComparison() {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    return useMutation({
        mutationFn: async (comparisonId: number) => {
            await apiClient.comparisons.deleteComparison({ id: comparisonId });
            return comparisonId;
        },
        onSuccess: (_data, comparisonId) => {
            queryClient.invalidateQueries({ queryKey: ['comparisons'] });
            queryClient.removeQueries({ queryKey: ['comparison', comparisonId] });
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
        onError: (error: Error) => {
            toast({ title: 'Failed to delete comparison', description: error.message, variant: 'destructive' });
        },
    });
}

export function useComparison(comparisonId: number, options?: { enabled?: boolean }) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ['comparison', comparisonId],
        queryFn: async () => {
            if (!comparisonId) return null;
            return await apiClient.comparisons.getComparisonDetails({ id: comparisonId });
        },
        enabled: options?.enabled !== undefined ? options.enabled : !!comparisonId,
        refetchInterval: (query) => {
            const data = query.state.data as any; // Cast to access status safely
            if (!data) return false;

            // Check for processing status to enable polling
            const status = data.status || data.comparison?.status;
            const isProcessing = status === ComparisonStatus.Processing || status === 'processing' || status === 'pending';

            return isProcessing ? 2000 : false;
        }
    });

    const data = query.data as any;
    const status = data?.status || data?.comparison?.status;
    const isProcessing = status === ComparisonStatus.Processing || status === 'processing' || status === 'pending';

    // Determine if we should connect to SSE
    const isCompleted = status === 'completed' || status === 'Completed';
    const isFailed = status === 'failed' || status === 'Failed';
    const isActive = !isCompleted && !isFailed;

    // Monitor SSE for completion/failure and invalidate queries
    const { isComplete: sseComplete, isFailed: sseFailed } = useComparisonMatchingSSE(comparisonId, isActive);

    useEffect(() => {
        if (sseComplete || sseFailed) {
            // Invalidate comparison queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['comparisons'] });
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
            // Invalidate notifications to show completion notification immediately in bell
            // (Backend publishes activity event, but this ensures instant UI update)
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    }, [sseComplete, sseFailed, comparisonId, queryClient]);

    // Mutations for varying levels of granularity
    const reanalyzeMutation = useMutation({
        mutationFn: async () => {
            // Placeholder for triggering a full re-analysis
            // await apiClient.comparisons.reanalyze(comparisonId);
            // For now we simulate a status change by invalidating
            await new Promise(resolve => setTimeout(resolve, 500));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    const updateMatchMutation = useMutation({
        mutationFn: async () => {
            // Placeholder: await apiClient.comparisons.updateMatch(vars.matchId, ...);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comparison', comparisonId] });
        }
    });

    return {
        ...query,
        comparison: data,
        status,
        isProcessing: isProcessing,
        // Actions
        reanalyze: reanalyzeMutation.mutate,
        isReanalyzing: reanalyzeMutation.isPending,
        updateMatch: updateMatchMutation.mutate
    };
}

export function useComparisonLogs(comparisonId: number | null | undefined, options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: ['comparison', comparisonId, 'logs'],
        queryFn: async () => {
            if (!comparisonId) return [];
            const logs = await apiClient.comparisons.getComparisonLogs({ id: comparisonId });
            // Normalize logs to ensure consistent format (string dates, no Date objects)
            return normalizeLogs(logs);
        },
        enabled: (options?.enabled ?? true) && !!comparisonId,
        staleTime: Infinity, // Logs don't change once completed
    });
}
